import { apiRequest } from "@/lib/api";

export interface AdminTeam {
  id: string;
  slug: string;
  name: string;
  status: string;
  plan: string;
  ownerPhone: string | null;
  memberCount: number;
  companyCount: number;
  subscriptionStatus: string | null;
  createdAt: string;
}

export interface AdminTeamDetail extends AdminTeam {
  members: AdminTeamMember[];
  invitations: AdminTeamInvitation[];
  companies: AdminCompanyRef[];
  recentSubscriptions: AdminSubscriptionRef[];
}

export interface AdminTeamMember {
  userId: string;
  phone: string | null;
  displayName: string | null;
  role: string;
  status: "active" | "suspended" | "removed";
  joinedAt: string;
}

export interface AdminTeamInvitation {
  id: string;
  phone: string;
  role: string;
  status: string;
  expiresAt: string;
  createdAt: string;
  acceptedUserId: string | null;
  acceptedAt: string | null;
}

export interface AdminCompanyRef {
  nit: string;
  legalName: string;
  certStatus: string | null;
  dianEnvironment: string;
  createdAt: string;
}

export interface AdminSubscriptionRef {
  id: string;
  status: string;
  plan: string;
  currentPeriodEnd: string | null;
  lastPaymentAt: string | null;
}

export interface PaginatedAdminTeams {
  teams: AdminTeam[];
  pagination: { total: number; page: number; pageSize: number; pageCount: number };
}

export interface ListAllTeamsParams {
  q?: string;
  status?: string;
  page?: number;
  pageSize?: number;
}

export function listAllTeams(
  params: ListAllTeamsParams,
): Promise<PaginatedAdminTeams> {
  return apiRequest("/api/admin/teams", {
    query: params as Record<string, unknown>,
  });
}

export function getTeamDetail(teamId: string): Promise<AdminTeamDetail> {
  return apiRequest(`/api/admin/teams/${teamId}`);
}

export interface CreateAdminTeamInput {
  name: string;
  ownerPhone: string;
}

export function createAdminTeam(input: CreateAdminTeamInput): Promise<AdminTeam> {
  return apiRequest("/api/admin/teams", {
    method: "POST",
    body: input,
  });
}

export function suspendTeam(teamId: string, reason: string): Promise<AdminTeam> {
  return apiRequest(`/api/admin/teams/${teamId}/suspend`, {
    method: "POST",
    body: { reason },
  });
}

export function reactivateTeam(teamId: string): Promise<AdminTeam> {
  return apiRequest(`/api/admin/teams/${teamId}/reactivate`, {
    method: "POST",
  });
}

// ── Member management (operator levers) ─────────────────────────────────────

export const INVITABLE_ROLES = ["admin", "accountant", "viewer"] as const;
export type InvitableRole = (typeof INVITABLE_ROLES)[number];

export interface InviteTeamMemberResult {
  invitationId: string;
  phone: string;
  role: InvitableRole;
  expiresAt: string;
  sentAt: string;
}

export function inviteTeamMember(
  teamId: string,
  input: { phone: string; role: InvitableRole },
): Promise<InviteTeamMemberResult> {
  return apiRequest(`/api/admin/teams/${teamId}/invitations`, {
    method: "POST",
    body: input,
  });
}

export function cancelTeamInvitation(
  teamId: string,
  invitationId: string,
): Promise<{ id: string; status: "cancelled" }> {
  return apiRequest(`/api/admin/teams/${teamId}/invitations/${invitationId}`, {
    method: "DELETE",
  });
}

export function suspendTeamMember(
  teamId: string,
  userId: string,
): Promise<{ member: AdminTeamMember }> {
  return apiRequest(`/api/admin/teams/${teamId}/members/${userId}/suspend`, {
    method: "POST",
  });
}

export function reactivateTeamMember(
  teamId: string,
  userId: string,
): Promise<{ member: AdminTeamMember }> {
  return apiRequest(`/api/admin/teams/${teamId}/members/${userId}/reactivate`, {
    method: "POST",
  });
}

export function removeTeamMember(
  teamId: string,
  userId: string,
): Promise<{ member: AdminTeamMember }> {
  return apiRequest(`/api/admin/teams/${teamId}/members/${userId}`, {
    method: "DELETE",
  });
}